import { DeepPartial } from "react-hook-form";
import { useAavoWizardStepForm } from "src/components/common/wizard/useAavoWizardStepForm.ts";
import {
	CustomerOfferFormContent,
	CustomerOfferFormValues,
} from "src/components/views/erp/sales/customerOrderOffer/CustomerOfferFormContent.tsx";
import { SalesLeadToOfferApi_InitData } from "src/api/generated/erp/sales/crm/salesLeads/api/salesLeadToOfferApi.ts";
import { DEFAULT_COUNTRY_CODE } from "src/components/common/country/countries.ts";
import { findDefaultVatHandlingType, getDefaultVatCode } from "src/components/views/erp/sales/salesUtils.ts";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import dayjs from "dayjs";
import { getDefaultDeliveryAddressOrNull } from "src/components/views/erp/sales/customerAddressUtils.ts";
import { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import { SalesLead } from "src/api/generated/erp/db/types/tables/salesLead.ts";

interface CustomerOfferWizardStepProps {
	initData: SalesLeadToOfferApi_InitData;
	defaultValues: DeepPartial<CustomerOfferFormValues>;
	onUpdate: (values: DeepPartial<CustomerOfferFormValues>) => void;
}

export const CustomerOfferWizardStep = ({ initData, defaultValues, onUpdate }: CustomerOfferWizardStepProps) => {
	const useAavoFormReturn = useAavoWizardStepForm<CustomerOfferFormValues>({
		defaultValues,
		onUpdate,
	});

	return (
		<CustomerOfferFormContent
			{...useAavoFormReturn}
			customerOrderOffer={null}
			initData={initData}
			customerAddresses={initData.customerAddresses}
			offerHasAnyLines={false}
			hideCustomerFields={true}
		/>
	);
};

export function getDefaultOfferValues(
	{ initData, defaultSiteId, appUserId, inputPricesWithVatByDefault, billingPlanEnabledByDefault }: {
		initData: SalesLeadToOfferApi_InitData,
		defaultSiteId: number,
		appUserId: number,
		inputPricesWithVatByDefault: boolean,
		billingPlanEnabledByDefault: boolean
	},
): DeepPartial<CustomerOfferFormValues> {
	const salesLead = initData.salesLead;
	return {
		siteId: defaultSiteId,
		customerId: salesLead.customerId ?? undefined,
		plannedDeliveryDate: dayJsToDateIsoString(dayjs()),
		responsiblePersonId: appUserId,
		salespersonId:
			salesLead.salespersonId ??
			initData.salespersonOptions.find((sp) => sp.appUserId === appUserId)?.salespersonId,
		currencyId:
			initData.customerCurrencyId ??
			initData.currencyOptions.find((currency) => currency.currencyCode === "EUR")?.currencyId,
		vatHandlingTypeId: findDefaultVatHandlingType(initData.vatHandlingTypeOptions)?.vatHandlingTypeId,
		vatCodeId: getDefaultVatCode(initData.vatCodeOptions).vatCodeId,
		inputPricesWithVat: inputPricesWithVatByDefault,
		billingPlanEnabled: billingPlanEnabledByDefault,
		paymentTermId: initData.paymentTermOptions.find((term) => term.isDefault)?.paymentTermId,
		customerPriceGroupId: initData.customerPriceGroupOptions.find((group) => group.isDefault)?.customerPriceGroupId,
		deliveryTermsId: initData.deliveryTermOptions.find((term) => term.isDefault)?.deliveryTermsId,
		deliveryMethodId: initData.deliveryMethodOptions[0]?.deliveryMethodId,
		...getDefaultAddressValues(salesLead, initData.customerAddresses),
	};
}

function getDefaultAddressValues(
	salesLead: SalesLead,
	customerAddresses: CustomerAddress[] | null | undefined,
): DeepPartial<CustomerOfferFormValues> {
	if (customerAddresses != null && customerAddresses.length > 0) {
		const delivery = getDefaultDeliveryAddressOrNull(customerAddresses);
		const invoice = customerAddresses.find((a) => a.addressType === "DOCUMENT" && a.isDefault);
		return {
			deliveryAddressId: delivery?.customerAddressId,
			deliveryName: delivery?.name ?? "",
			deliveryRecipientName: delivery?.recipientName ?? "",
			deliveryAddress_1: delivery?.address_1 ?? "",
			deliveryAddress_2: delivery?.address_2 ?? "",
			deliveryPostalCode: delivery?.postalCode ?? "",
			deliveryCity: delivery?.city ?? "",
			deliveryCountry: delivery?.country ?? DEFAULT_COUNTRY_CODE,
			deliveryContact: delivery != null ? [delivery.contact, delivery.phone].filter(Boolean).join(" ") : "",
			...(delivery?.vatHandlingTypeId != null && {
				vatHandlingTypeId: delivery.vatHandlingTypeId,
			}),
			invoiceAddressId: invoice?.customerAddressId,
			contactName: delivery?.contact ?? "",
			contactEmail: delivery?.email ?? "",
			contactPhone: delivery?.phone ?? "",
		};
	} else {
		return {
			deliveryAddress_1: salesLead.address_1,
			deliveryAddress_2: salesLead.address_2,
			deliveryPostalCode: salesLead.postalCode,
			deliveryCity: salesLead.city,
			deliveryCountry: salesLead.country ?? DEFAULT_COUNTRY_CODE,
		};
	}
}
